import type { PermissionKey } from "@/components/Security/ProtectedRoute";

import { logger } from "@/telemetry";

interface AccessAttempt {
  route: string;
  permission: PermissionKey;
  granted: boolean;
  timestamp?: string;
  userAgent?: string;
}

/**
 * Logs access attempts for audit purposes.
 *
 * This function is intentionally non-blocking to avoid adding latency
 * to route navigation. It logs access attempts asynchronously.
 *
 * In production, this could be extended to:
 * - Send audit logs to a backend service
 * - Store in a centralised logging system
 * - Trigger alerts for suspicious access patterns
 */
export const logAccessAttempt = (attempt: AccessAttempt): void => {
  const logEntry = {
    ...attempt,
    timestamp: attempt.timestamp || new Date().toISOString(),
    userAgent: attempt.userAgent || navigator.userAgent,
  };

  // Log for development/debugging
  if (attempt.granted) {
    logger.info(`[ACCESS AUDIT] ✓ Granted access to ${attempt.route}`, logEntry);
  } else {
    logger.warn(
      `[ACCESS AUDIT] ✗ Denied access to ${attempt.route} - missing '${attempt.permission}' permission`,
      logEntry,
    );
  }

  // Store denied attempts in sessionStorage for potential security review
  if (!attempt.granted) {
    storeDeniedAttempt(logEntry);
  }
};

/**
 * Stores denied access attempts in sessionStorage for security review.
 * Limited to last 50 attempts to prevent storage bloat.
 */
const storeDeniedAttempt = (attempt: AccessAttempt & { timestamp: string }): void => {
  const STORAGE_KEY = "_access_audit_denied";
  const MAX_STORED_ATTEMPTS = 50;

  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    const attempts: (AccessAttempt & { timestamp: string })[] = existing
      ? JSON.parse(existing)
      : [];

    attempts.push(attempt);

    // Keep only the most recent attempts
    const trimmed = attempts.slice(-MAX_STORED_ATTEMPTS);

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    // Silently fail - audit logging should not break the application
    logger.error(
      "[ACCESS AUDIT] Failed to store denied attempt",
      error instanceof Error ? error : undefined,
    );
  }
};

/**
 * Retrieves stored denied access attempts for security review.
 * Useful for debugging or security audits.
 */
export const getDeniedAttempts = (): (AccessAttempt & { timestamp: string })[] => {
  const STORAGE_KEY = "_access_audit_denied";

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

/**
 * Clears stored denied access attempts.
 */
export const clearDeniedAttempts = (): void => {
  const STORAGE_KEY = "_access_audit_denied";

  sessionStorage.removeItem(STORAGE_KEY);
};

/**
 * Gets a summary of denied attempts for the current session.
 * Useful for security dashboards or alerts.
 */
export const getDeniedAttemptsSummary = (): {
  total: number;
  byRoute: Record<string, number>;
  byPermission: Record<string, number>;
} => {
  const attempts = getDeniedAttempts();
  const byRoute: Record<string, number> = {};
  const byPermission: Record<string, number> = {};

  for (const attempt of attempts) {
    byRoute[attempt.route] = (byRoute[attempt.route] || 0) + 1;
    byPermission[attempt.permission] = (byPermission[attempt.permission] || 0) + 1;
  }

  return {
    total: attempts.length,
    byRoute,
    byPermission,
  };
};
